'use client';

/**
 * "Create a free account" — the prompt a guest sees instead of the login wall.
 *
 * Since 2026-10-06 a signed-out visitor gets a small number of free
 * translations (backend lib/guestCredits.ts) and uses the real translator. This
 * opens when:
 *   • 'limit'    — a translation was refused because the free ones are used up;
 *   • 'feature'  — they reached something that needs an account (voice, image,
 *                  feedback);
 *   • 'settings' — they pressed the gear, whose tabs all belong to an account.
 *
 * Opened by a window event (GUEST_PROMPT_EVENT in lib/api.ts) so the API layer,
 * which is not a component, can ask for it. Closable — it is an invitation; a
 * guest at 0 who closes it simply gets it again on the next translation.
 */

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useAuth } from '@/lib/auth';
import { GUEST_PROMPT_EVENT } from '@/lib/api';
import { BRAND } from '@/lib/brand';
import { RedditMark } from './AuthForm';
import { S_BTN, S_BTN_PRIMARY } from './settingsUi';
import Layer from './Layer';

type Reason = 'limit' | 'feature' | 'settings' | 'reward' | 'buy';

export function openGuestPrompt(reason: Reason): void {
  window.dispatchEvent(new CustomEvent(GUEST_PROMPT_EVENT, { detail: { reason } }));
}

export default function GuestPrompt() {
  const { user, guest } = useAuth();
  const [reason, setReason] = useState<Reason | null>(null);
  const primary = useRef<HTMLAnchorElement | null>(null);

  useEffect(() => {
    const onPrompt = (e: Event) => {
      const r = (e as CustomEvent).detail?.reason;
      setReason(r === 'feature' || r === 'settings' || r === 'reward' || r === 'buy' ? r : 'limit');
    };
    window.addEventListener(GUEST_PROMPT_EVENT, onPrompt);
    return () => window.removeEventListener(GUEST_PROMPT_EVENT, onPrompt);
  }, []);

  useEffect(() => {
    if (!reason) return;
    primary.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setReason(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [reason]);

  // Signed in (in another tab, say) — nothing to invite them to.
  if (!reason || user) return null;

  const total = guest?.total ?? 0;
  // Short and specific: what they hit, and what an account gives them (owner 2026-10-12 redesign).
  const copy: Record<Reason, { title: string; body: string }> = {
    limit: { title: `You've used your ${total} free translations`, body: `Create a free account to keep translating with ${BRAND.name}.` },
    reward: { title: 'Daily rewards need an account', body: 'Sign up to collect KHARMA every day and spend it on translations and games.' },
    buy: { title: 'Premium games need an account', body: 'Games are bought with KHARMA. Sign up — you start with a balance and earn more every day.' },
    feature: { title: 'This needs a free account', body: 'Voice, image reading and feedback come with a free account.' },
    settings: { title: 'Settings belong to an account', body: 'Sign up to keep your nickname, photo and preferences on every device.' },
  };
  const { title, body } = copy[reason];
  const next = typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/';

  return (
    <Layer>
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/45 p-4 backdrop-blur-[2px]"
      onMouseDown={(e) => { if (e.target === e.currentTarget) setReason(null); }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="gt-guest-title"
    >
      <div className="relative w-full max-w-[380px] rounded-2xl border border-ink-200 bg-white p-7
                      shadow-[0_30px_90px_-25px_rgba(0,0,0,0.45)] dark:border-white/10 dark:bg-ink-900">
        <button
          type="button"
          onClick={() => setReason(null)}
          aria-label="Close"
          className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-lg text-ink-400 transition
                     hover:bg-ink-100 hover:text-ink-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-400
                     dark:hover:bg-white/5 dark:hover:text-white"
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8"
               strokeLinecap="round" aria-hidden>
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>

        <span className="grid h-11 w-11 place-items-center rounded-xl border border-ink-200 bg-ink-100 text-ink-700
                         dark:border-white/10 dark:bg-white/5 dark:text-ink-dim" aria-hidden>
          <ReasonIcon reason={reason} />
        </span>
        <h2 id="gt-guest-title" className="mt-4 pr-6 text-lg font-semibold tracking-tight text-ink-900 dark:text-white">
          {title}
        </h2>
        <p className="mt-1.5 text-sm leading-relaxed text-ink-500 dark:text-ink-400">{body}</p>

        <div className="mt-6 grid gap-2">
          <a href={`/api/auth/reddit/start?next=${encodeURIComponent(next)}`} className={S_BTN + ' h-10 w-full'}>
            <RedditMark className="h-5 w-5" />
            Join with Reddit
          </a>
          <Link ref={primary} href="/register" className={S_BTN_PRIMARY + ' h-10 w-full'}>
            Create free account
          </Link>
        </div>
        <p className="mt-4 text-center text-sm text-ink-500 dark:text-ink-400">
          Have an account?{' '}
          <Link href="/login" className="font-medium text-ink-900 underline-offset-4 hover:underline dark:text-white">Sign in</Link>
        </p>
      </div>
    </div>
    </Layer>
  );
}

/** One quiet line icon per reason. */
function ReasonIcon({ reason }: { reason: Reason }) {
  const p = { viewBox: '0 0 24 24', className: 'h-5 w-5', fill: 'none', stroke: 'currentColor', strokeWidth: 1.7, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  if (reason === 'buy') return <svg {...p}><rect x="3" y="7" width="18" height="12" rx="3" /><path d="M8 13h3M9.5 11.5v3" /><circle cx="15.5" cy="12" r=".6" fill="currentColor" /><circle cx="17" cy="14" r=".6" fill="currentColor" /></svg>;
  if (reason === 'reward') return <svg {...p}><rect x="3.5" y="9" width="17" height="11" rx="1.5" /><path d="M2.5 9h19M12 9v11M12 9c-1.5-3.5-5-4-5-1.5S10 9 12 9zm0 0c1.5-3.5 5-4 5-1.5S14 9 12 9z" /></svg>;
  if (reason === 'limit') return <svg {...p}><path d="M4 6h8M8 4v2M5 6c.5 3 3 6 6 7.5M11 6c-.5 3-3 6-6 7.5" /><path d="M12.5 20l4-9 4 9M14 17h5" /></svg>;
  if (reason === 'settings') return <svg {...p}><circle cx="12" cy="8.5" r="3.5" /><path d="M5 20a7 7 0 0114 0" /></svg>;
  return <svg {...p}><rect x="5" y="10.5" width="14" height="10" rx="2" /><path d="M8.5 10.5V8a3.5 3.5 0 017 0v2.5" /></svg>;
}
